/*
 * Every word on the landing, in English and German.
 *
 * App vocabulary comes from apps/mobile/src/i18n (Kochmodus, Gefrierfach, the base and
 * dish names, the import stages), so the page and the app say the same thing. German
 * uses "du", like the app. Numbers live in LandingPage; only words live here.
 */

export type Lang = 'en' | 'de';
export const LANGS: Lang[] = ['en', 'de'];

const en = {
  langName: 'English',
  locale: 'en-IE',
  title: 'Chefty: three bases, five different lunches',
  skip: 'Skip to content',
  backToTop: 'Chefty, back to top',
  langLabel: 'Language',
  theme: { toLight: 'Switch to light theme', toDark: 'Switch to dark theme' },
  nav: {
    week: 'The week',
    how: 'How it works',
    protein: 'Protein',
    pricing: 'Pricing',
    faq: 'FAQ',
  },
  cta: {
    appStore: 'Download on the App Store',
    testflight: 'Get it on TestFlight',
    invite: 'Get a beta invite',
    inviteSubject: 'Chefty beta invite',
  },
  points: {
    freeToStart: 'Free to start',
    freeBeta: 'Free iPhone beta',
    noAccount: 'No account needed',
    reply: 'We reply with a TestFlight link',
  },

  days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  monday: 'Monday',
  week: [
    { dish: 'Bowl Teriyaki', short: 'Bowl', from: 'Teriyaki + quinoa' },
    { dish: 'Wrap with quinoa', short: 'Wrap', from: 'Lentils + quinoa' },
    { dish: 'Teriyaki salad', short: 'Salad', from: 'Teriyaki + quinoa' },
    { dish: 'Quinoa pan', short: 'Pan', from: 'Lentils + quinoa' },
    { dish: 'Soup', short: 'Soup', from: 'Teriyaki + quinoa' },
  ],
  bases: {
    teriyaki: {
      name: 'Chicken teriyaki',
      short: 'Teriyaki',
      alt: 'Chicken teriyaki, sliced, with spring onion and sesame in a white bowl',
    },
    lachs: { name: 'Baked salmon', short: 'Salmon', alt: 'Baked salmon fillet with lemon' },
    linsen: { name: 'Lentil dal', short: 'Lentils', alt: 'Red lentil dal in a white bowl' },
    quinoa: {
      name: 'Quinoa with feta',
      short: 'Quinoa',
      alt: 'Quinoa with feta and cucumber in a white bowl',
    },
    reis: { name: 'Rice', short: 'Rice', alt: 'Cooked rice in a white bowl' },
    ofengemuese: {
      name: 'Roast veg',
      short: 'Roast veg',
      alt: 'Roast vegetables: broccoli, pepper, courgette and red onion',
    },
  },
  slot: { Protein: 'Protein', Carb: 'Carb', Veg: 'Veg' },
  protein: 'protein',
  perPortion: (g: number) => `${g} g protein / portion`,
  fridge: 'Fridge',
  freezer: 'Freezer',
  fridgeDays: (n: number) => `Fridge ${n} ${n === 1 ? 'day' : 'days'}`,
  goals: [
    { name: 'Get lighter', sub: 'smaller portions, more protein', short: 'smaller portions' },
    { name: 'Stay fit', sub: 'portions as usual', short: 'portions as usual' },
    { name: 'Build muscle', sub: 'bigger portions, lots of protein', short: 'bigger portions' },
  ],

  hero: {
    tag: 'Meal prep for iPhone',
    eyebrow: 'Free to start',
    verbs: ['Cook.', 'Freeze.', 'Thaw.', 'Mix\u00a0it\u00a0up.'],
    lead: 'One cook on your day. Chefty splits it between fridge and freezer, tells you the night before what to take out, and turns the same three bases into a different lunch each day.',
    stepsLabel: 'A prep week in four steps',
    steps: [
      {
        h: 'Cook once',
        p: 'Three bases, step by step in Cooking Mode. Chefty splits them into lunch boxes.',
        when: 'Sun · about 90 min',
        alt: 'A pot and four glass lunch boxes: teriyaki with rice, lentils, quinoa with vegetables, salmon',
        cold: false,
        protein: false,
      },
      {
        h: 'Freeze the rest',
        p: 'Cooked food keeps about three days. Chefty says which boxes go in the freezer.',
        when: 'Thu · Fri',
        alt: 'Two frosted lunch boxes in an open freezer drawer',
        cold: true,
        protein: false,
      },
      {
        h: 'Thaw the night before',
        p: 'The Week tab tells you which box to take out tonight. Into the fridge overnight, ready by lunch.',
        when: 'Wed evening',
        alt: 'One lunch box on a fridge shelf, an alarm clock at eight and a crescent moon',
        cold: true,
        protein: false,
      },
      {
        h: 'A different lunch',
        p: 'Thursday it’s a quinoa pan, not Monday’s bowl again.',
        when: 'Thu',
        alt: 'An open lunch box with quinoa, chicken, vegetables and avocado, a fork beside it',
        cold: false,
        protein: true,
      },
    ],
    foot: 'Estimated per portion. An example week with 5 lunches, the app’s default.',
  },

  before: {
    kicker: 'The usual week',
    h2: 'Meal prep without the same box five times.',
    beforeHead: 'Most prep Sundays',
    beforeLines: [
      'One big pot, portioned into five boxes.',
      'By Wednesday nobody wants it.',
      'Day 5 sits in the fridge too long.',
      'Protein is a guess.',
    ],
    afterHead: 'With Chefty',
    afterLines: [
      'Three bases, cooked once.',
      'Bowl, wrap, salad, pan, soup.',
      'Later days go in the freezer on prep day.',
      'The grams on every dish.',
    ],
  },

  how: {
    kicker: 'How it works',
    h2: 'Four questions, three bases, one list.',
    s1: {
      h: 'Say what you’re prepping for.',
      p: 'Lighter, fit, or more muscle. Chefty sets portions to match.',
      q: 'What are you prepping for?',
    },
    s2: {
      h: 'Pick your prep day and how many lunches.',
      p: 'For one person or more.',
      q: 'Who for, and how often?',
      people: 'People',
      lunches: 'Lunches a week',
      prepDay: 'Prep day',
      hint: 'By day 4 at the latest, it goes in the freezer.',
    },
    s3: {
      h: 'Swipe three bases.',
      p: 'Teriyaki chicken, lentils, salmon, roast veg, rice, quinoa. Leave out what you never eat.',
      q: 'Would you eat this as a base?',
      skip: 'Skip',
      take: 'Take',
    },
    s4: {
      h: 'Get the week and the list.',
      p: 'A different dish each day, the protein on each, the shopping list sorted by aisle.',
      bases: '3 bases,',
      dishes: 'dishes.',
      variety: 'No two days alike.',
    },
  },

  prepWeek: {
    kicker: 'The prep week',
    h2: 'Six bases to start. Pick three.',
    lead: 'Each base is cooked once on your prep day. During the week it turns up in a different dish each day.',
    foot: 'Estimated per portion. Fridge days count from your prep day.',
    prev: 'Previous bases',
    next: 'More bases',
    freezerH: 'It tells you what goes in the freezer.',
    freezerP:
      'Cooked food keeps about three days in the fridge. Chefty puts the later days in the freezer on prep day and marks them in your week.',
    strip: 'Mon–Wed fridge · Thu–Fri freezer',
    time: 'Sunday · about 90 min',
    dogAlt: 'The Chefty dog standing behind four prepped lunch boxes',
  },

  proteinSec: {
    kicker: 'Protein, as a number',
    h2: 'The protein is on every dish.',
    lead: 'protein per lunch, on average, in the example week above.',
    body: 'Each dish shows its grams per portion, and the week shows your total. No logging, no diary.',
    barsLabel: 'Protein per lunch',
    goalsLabel: (dish: string) => `One portion of ${dish} by goal`,
    foot: (dish: string) =>
      `${dish}, one portion, by goal. Values are estimates per portion and not medical or nutrition advice.`,
  },

  recipes: {
    kicker: 'Your own recipes',
    h2: 'Got recipes saved everywhere? Bring them.',
    importH: 'From TikTok, Instagram, YouTube or any recipe site.',
    importP:
      'Paste the link and Chefty pulls out the recipe. A screenshot or plain text works too.',
    reviewH: 'Nothing is saved until you’ve checked it.',
    reviewP: 'So a misread “1 tbsp” never becomes “1 cup” in your book.',
    reviewLabel:
      'The review screen: the dog says Found it all. You check, I’ll wait. Below, the Shakshuka draft with editable ingredients.',
    listH: 'The shopping list, sorted by aisle.',
    listP: 'So prep day starts with one trip, not three.',
    listHead: 'Everything for your week.',
    items: 'items.',
    counter: (n: number, total: number) => `${n} of ${total}`,
    aisles: { produce: 'Produce', chilled: 'Chilled' },
    shopping: {
      springOnion: 'Spring onion',
      ginger: 'Ginger',
      cucumber: 'Cucumber',
      chicken: 'Chicken thighs',
      feta: 'Feta',
    },
    factsH: 'Honest terms.',
    facts: [
      ['Start without an account.', ' Sign in with Apple, Google or email when you want sync.'],
      ['Saved recipes work offline.', ''],
      ['No ads on any plan.', ''],
      ['Analytics only if you say yes.', ' Your screen is never recorded.'],
      ['Delete your account in the app.', ''],
      ['English and German.', ''],
    ],
    smallprint:
      'Your imported recipes live in your book and in Cooking Mode. They don’t feed the prep week yet.',
  },
  shakshuka: {
    title: 'Shakshuka',
    hello: 'Found it all. You check, I’ll wait.',
    link: 'Link',
    readByAi: 'Read by AI. Tap to change.',
    ingredients: 'Ingredients',
    method: 'Method',
    addIngredient: 'Add ingredient',
    items: [
      ['4', 'Eggs'],
      ['400 g', 'Chopped tomatoes'],
      ['1', 'Red pepper'],
      ['1 tsp', 'Cumin'],
      ['½', 'Bunch parsley'],
    ],
    steps: [
      'Soften the onion and pepper in olive oil for 5 minutes.',
      'Add cumin and tomatoes. Simmer for 10 minutes.',
      'Make four hollows and crack in the eggs.',
    ],
    save: 'Save',
    summary: (ingredients: number, steps: number) => `${ingredients} ingredients · ${steps} steps`,
  },
  importDemo: {
    sources: {
      tiktok: 'TikTok',
      instagram: 'Instagram',
      youtube: 'YouTube',
      web: 'Recipe sites',
      photo: 'Photos',
      text: 'Text',
    },
    demos: [
      {
        source: 'tiktok',
        link: 'tiktok.com/@yourfavcook/video/7391827465',
        title: 'Shakshuka',
        items: [
          ['4', 'Eggs'],
          ['400 g', 'Chopped tomatoes'],
          ['1', 'Red pepper'],
          ['1 tsp', 'Cumin'],
          ['½', 'Bunch parsley'],
        ],
        steps: 6,
      },
      {
        source: 'instagram',
        link: 'instagram.com/reel/C9f2kLmQx4p',
        title: 'Baked feta pasta',
        items: [
          ['200 g', 'Feta'],
          ['400 g', 'Cherry tomatoes'],
          ['250 g', 'Pasta'],
          ['2', 'Garlic cloves'],
          ['', 'Basil'],
        ],
        steps: 4,
      },
      {
        source: 'youtube',
        link: 'youtube.com/shorts/x8Qp3TvLm2c',
        title: 'Overnight oats',
        items: [
          ['50 g', 'Oats'],
          ['150 ml', 'Milk'],
          ['100 g', 'Yoghurt'],
          ['1 tbsp', 'Chia seeds'],
          ['', 'Berries'],
        ],
        steps: 3,
      },
      {
        source: 'web',
        link: 'grandmas-kitchen-tales.com/banana-bread',
        title: 'Banana bread',
        items: [
          ['3', 'Ripe bananas'],
          ['250 g', 'Flour'],
          ['100 g', 'Butter'],
          ['2', 'Eggs'],
          ['1 tsp', 'Baking soda'],
        ],
        steps: 6,
      },
    ],
    placeholder: 'Paste a link here',
    paste: 'Paste',
    import: 'Import',
    stages: ['Sending', 'Reading', 'Done'],
    lines: {
      send: 'Sending…',
      read: 'Reading ingredients and steps…',
      done: 'Done.',
      usually: 'Usually under 30 seconds.',
    },
    label:
      'Recipe links from TikTok, Instagram, YouTube and a recipe site are pasted and imported in turn. Chefty reads the ingredients and steps and shows each draft for you to check.',
  },

  cook: {
    kicker: 'Cooking Mode',
    h2: 'One step per screen. The ingredients you need are right there.',
    list: [
      ['Swipe to the next step.', 'Big type you can read from the hob.'],
      ['Tap a time to start a timer.', '“8 min” in the text is a button.'],
      ['Put the phone down.', 'It remembers where you were.'],
    ],
    phoneLabel: 'Cooking Mode for Shakshuka. Use Back and Next to go through the steps.',
    counter: (n: number, total: number) => `Step ${n} of ${total}`,
    stepLabel: (n: number) => `Shakshuka · Step ${n}`,
    timerStep: (n: number) => `· Step ${n}`,
    reach: 'What you’ll reach for',
    steps: [
      {
        text: ['Soften the onion and pepper in olive oil for', '5 min', '.'],
        reach: [
          ['1', 'Onion'],
          ['1', 'Red pepper'],
          ['', 'Olive oil'],
        ],
      },
      { text: ['Stir in the cumin and toast for', '1 min', '.'], reach: [['1 tsp', 'Cumin']] },
      {
        text: ['Add the tomatoes and simmer for', '10 min', '.'],
        reach: [['400 g', 'Chopped tomatoes']],
      },
      {
        text: ['Crack the eggs into the sauce, cover and cook for', '8 min', '.'],
        reach: [['4', 'Eggs']],
      },
      { text: ['Scatter the parsley over the top.', '', ''], reach: [['½', 'Bunch parsley']] },
      {
        text: ['Season with salt and pepper and serve from the pan.', '', ''],
        reach: [
          ['', 'Salt'],
          ['', 'Pepper'],
        ],
      },
    ],
    back: 'Back',
    next: 'Next step',
    finish: 'Done',
    startOver: 'Start over',
  },

  pricing: {
    kicker: 'Pricing',
    h2: 'Free to plan. Pay only for unlimited imports.',
    free: 'Free',
    freeLines: (imports: number, days: number) => [
      'Your prep week and the shopping list',
      'Cooking Mode and your recipe book',
      `${imports} recipe imports every ${days} days`,
      'Saved recipes never lock',
    ],
    plus: 'Chefty Plus',
    perMonth: 'a month',
    alt: (yearly: string, perMonth: string, trialDays: number) =>
      `or ${yearly} a year (${perMonth} a month), with a ${trialDays}-day free trial`,
    plusLines: [
      'Everything in Free',
      'Unlimited recipe imports',
      'Cancel any time in your Apple account',
    ],
    foot: 'Billed and cancelled through the App Store, in your App Store currency.',
  },

  faq: {
    kicker: 'Questions',
    h2: 'Before you prep.',
    write: 'Something else? Write to',
    items: (imports: number, days: number) => [
      {
        q: 'Do I have to cook every day?',
        a: 'No. You cook once on your prep day. The other days you combine what’s in the fridge into a different dish.',
      },
      {
        q: 'How long does prepped food keep?',
        a: 'Chefty plans about three days in the fridge. Lunches after that go in the freezer on prep day, and the week shows which ones.',
      },
      {
        q: 'Can I use my own recipes in the prep week?',
        a: 'Not yet. The prep week uses Chefty’s bases today. Your imported recipes go into your recipe book and Cooking Mode.',
      },
      {
        q: 'How exact are the protein numbers?',
        a: 'They are estimates per portion for each base. Use them as a guide, not as nutrition advice.',
      },
      {
        q: 'I’m vegetarian, or I don’t eat fish.',
        a: 'Mark what never goes in the box and Chefty leaves it out. Please check ingredients for allergens yourself.',
      },
      {
        q: 'Which recipe links work?',
        a: 'Most recipe blogs, magazine sites and YouTube. TikTok and Instagram work when the recipe is in the caption. A screenshot of the ingredient list always works.',
      },
      {
        q: 'What’s free?',
        a: `Planning your prep week, the shopping list, Cooking Mode and ${imports} recipe imports every ${days} days. The counter resets on its own, and saved recipes never lock.`,
      },
      {
        q: 'Do I need an account?',
        a: 'No. Start straight away. Sign in with Apple, Google or email later and your recipes come with you.',
      },
      { q: 'Is there an Android version?', a: 'Not yet. Chefty is on iPhone for now.' },
    ],
  },

  closing: {
    h2: 'Plan your first prep week tonight.',
    lede: 'Four questions, three bases, one list.',
    dogAlt: 'The Chefty dachshund sitting in a white chef’s toque',
  },

  footer: {
    tagline: 'Prep once, eat something different every day.',
    product: 'Product',
    help: 'Help',
    legal: 'Legal',
    prepWeek: 'The prep week',
    how: 'How it works',
    pricing: 'Pricing',
    faq: 'FAQ',
    support: 'Support',
    deleteAccount: 'Delete account',
    privacy: { label: 'Privacy', href: '/privacy' },
    terms: { label: 'Terms', href: '/terms' },
    cookies: 'Cookie settings',
    vat: 'VAT ID',
    responsible: 'Responsible for content per § 18 (2) MStV:',
    trademarks:
      'App Store and TestFlight are trademarks of Apple Inc. TikTok, Instagram and YouTube are trademarks of their respective owners.',
  },
};

export type Copy = typeof en;

const de: Copy = {
  langName: 'Deutsch',
  locale: 'de-DE',
  title: 'Chefty: drei Basen, fünf verschiedene Mittagessen',
  skip: 'Zum Inhalt springen',
  backToTop: 'Chefty, zurück nach oben',
  langLabel: 'Sprache',
  theme: { toLight: 'Zum hellen Design wechseln', toDark: 'Zum dunklen Design wechseln' },
  nav: {
    week: 'Die Woche',
    how: 'So geht’s',
    protein: 'Protein',
    pricing: 'Preise',
    faq: 'FAQ',
  },
  cta: {
    appStore: 'Laden im App Store',
    testflight: 'Über TestFlight testen',
    invite: 'Beta-Einladung holen',
    inviteSubject: 'Chefty Beta-Einladung',
  },
  points: {
    freeToStart: 'Kostenlos starten',
    freeBeta: 'Kostenlose iPhone-Beta',
    noAccount: 'Kein Konto nötig',
    reply: 'Wir antworten mit einem TestFlight-Link',
  },

  days: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  monday: 'Montag',
  week: [
    { dish: 'Bowl Teriyaki', short: 'Bowl', from: 'Teriyaki + Quinoa' },
    { dish: 'Wrap mit Quinoa', short: 'Wrap', from: 'Linsen + Quinoa' },
    { dish: 'Salat Teriyaki', short: 'Salat', from: 'Teriyaki + Quinoa' },
    { dish: 'Quinoapfanne', short: 'Pfanne', from: 'Linsen + Quinoa' },
    { dish: 'Suppe', short: 'Suppe', from: 'Teriyaki + Quinoa' },
  ],
  bases: {
    teriyaki: {
      name: 'Hähnchen-Teriyaki',
      short: 'Teriyaki',
      alt: 'Hähnchen-Teriyaki in Scheiben, mit Frühlingszwiebel und Sesam in einer weißen Schüssel',
    },
    lachs: { name: 'Ofenlachs', short: 'Lachs', alt: 'Gebackenes Lachsfilet mit Zitrone' },
    linsen: {
      name: 'Linsen-Dal',
      short: 'Linsen',
      alt: 'Rotes Linsen-Dal in einer weißen Schüssel',
    },
    quinoa: {
      name: 'Quinoa mit Feta',
      short: 'Quinoa',
      alt: 'Quinoa mit Feta und Gurke in einer weißen Schüssel',
    },
    reis: { name: 'Reis', short: 'Reis', alt: 'Gekochter Reis in einer weißen Schüssel' },
    ofengemuese: {
      name: 'Ofengemüse',
      short: 'Ofengemüse',
      alt: 'Ofengemüse: Brokkoli, Paprika, Zucchini und rote Zwiebel',
    },
  },
  slot: { Protein: 'Protein', Carb: 'Kohlenhydrate', Veg: 'Gemüse' },
  protein: 'Protein',
  perPortion: (g) => `${g} g Protein / Portion`,
  fridge: 'Kühlschrank',
  freezer: 'Gefrierfach',
  fridgeDays: (n) => `Kühlschrank: ${n} ${n === 1 ? 'Tag' : 'Tage'}`,
  goals: [
    {
      name: 'Leichter werden',
      sub: 'kleinere Portionen, mehr Protein',
      short: 'kleinere Portionen',
    },
    { name: 'Fit bleiben', sub: 'Portionen wie gewohnt', short: 'Portionen wie gewohnt' },
    {
      name: 'Muskeln aufbauen',
      sub: 'größere Portionen, viel Protein',
      short: 'größere Portionen',
    },
  ],

  hero: {
    tag: 'Meal Prep fürs iPhone',
    eyebrow: 'Kostenlos starten',
    verbs: ['Kochen.', 'Einfrieren.', 'Auftauen.', 'Neu\u00a0kombinieren.'],
    lead: 'Einmal kochen, an deinem Tag. Chefty teilt alles auf Kühlschrank und Gefrierfach auf, sagt dir am Vorabend, was du rausnimmst, und macht aus denselben drei Basen jeden Tag ein anderes Mittagessen.',
    stepsLabel: 'Eine Prep-Woche in vier Schritten',
    steps: [
      {
        h: 'Einmal kochen',
        p: 'Drei Basen, Schritt für Schritt im Kochmodus. Chefty teilt sie auf deine Boxen auf.',
        when: 'So · ca. 90 Min',
        alt: 'Ein Topf und vier Glasboxen: Teriyaki mit Reis, Linsen, Quinoa mit Gemüse, Lachs',
        cold: false,
        protein: false,
      },
      {
        h: 'Den Rest einfrieren',
        p: 'Gekochtes hält etwa drei Tage. Chefty sagt dir, welche Boxen ins Gefrierfach kommen.',
        when: 'Do · Fr',
        alt: 'Zwei bereifte Glasboxen in einer offenen Gefrierschublade',
        cold: true,
        protein: false,
      },
      {
        h: 'Am Vorabend auftauen',
        p: 'Der Tab Woche sagt dir, welche Box du heute Abend rausnimmst. Über Nacht in den Kühlschrank, mittags fertig.',
        when: 'Mi abends',
        alt: 'Eine Glasbox im Kühlschrank, daneben ein Wecker auf acht und ein Mond',
        cold: true,
        protein: false,
      },
      {
        h: 'Jeden Tag was anderes',
        p: 'Donnerstag gibt es Quinoapfanne, nicht wieder die Bowl vom Montag.',
        when: 'Do',
        alt: 'Eine offene Glasbox mit Quinoa, Hähnchen, Gemüse und Avocado, daneben eine Gabel',
        cold: false,
        protein: true,
      },
    ],
    foot: 'Geschätzt pro Portion. Eine Beispielwoche mit 5 Mittagessen, dem Standard der App.',
  },

  before: {
    kicker: 'Die übliche Woche',
    h2: 'Meal Prep ohne fünfmal dieselbe Box.',
    beforeHead: 'Die meisten Prep-Sonntage',
    beforeLines: [
      'Ein großer Topf, auf fünf Boxen verteilt.',
      'Am Mittwoch will es keiner mehr.',
      'Tag 5 steht zu lange im Kühlschrank.',
      'Protein ist Schätzsache.',
    ],
    afterHead: 'Mit Chefty',
    afterLines: [
      'Drei Basen, einmal gekocht.',
      'Bowl, Wrap, Salat, Pfanne, Suppe.',
      'Spätere Tage kommen am Prep-Tag ins Gefrierfach.',
      'Die Gramm stehen bei jedem Gericht.',
    ],
  },

  how: {
    kicker: 'So geht’s',
    h2: 'Vier Fragen, drei Basen, eine Liste.',
    s1: {
      h: 'Sag, wofür du preppst.',
      p: 'Leichter, fit oder mehr Muskeln. Chefty passt die Portionen an.',
      q: 'Wofür preppst du?',
    },
    s2: {
      h: 'Wähl deinen Prep-Tag und wie viele Mittagessen.',
      p: 'Für eine Person oder mehr.',
      q: 'Für wen und wie oft?',
      people: 'Personen',
      lunches: 'Mittagessen pro Woche',
      prepDay: 'Prep-Tag',
      hint: 'Spätestens ab Tag 4 kommt es ins Gefrierfach.',
    },
    s3: {
      h: 'Wisch dich durch drei Basen.',
      p: 'Teriyaki-Hähnchen, Linsen, Lachs, Ofengemüse, Reis, Quinoa. Lass weg, was du nie isst.',
      q: 'Würdest du das als Basis essen?',
      skip: 'Auslassen',
      take: 'Nehmen',
    },
    s4: {
      h: 'Hol dir die Woche und die Liste.',
      p: 'Jeden Tag ein anderes Gericht, bei jedem das Protein, die Einkaufsliste nach Gängen sortiert.',
      bases: '3 Basen,',
      dishes: 'Gerichte.',
      variety: 'Kein Tag wie der andere.',
    },
  },

  prepWeek: {
    kicker: 'Die Prep-Woche',
    h2: 'Sechs Basen zum Start. Nimm drei.',
    lead: 'Jede Basis wird einmal an deinem Prep-Tag gekocht. Unter der Woche taucht sie jeden Tag in einem anderen Gericht auf.',
    foot: 'Geschätzt pro Portion. Die Kühlschranktage zählen ab deinem Prep-Tag.',
    prev: 'Vorherige Basen',
    next: 'Weitere Basen',
    freezerH: 'Chefty sagt dir, was ins Gefrierfach kommt.',
    freezerP:
      'Gekochtes hält sich etwa drei Tage im Kühlschrank. Chefty legt die späteren Tage am Prep-Tag ins Gefrierfach und markiert sie in deiner Woche.',
    strip: 'Mo–Mi Kühlschrank · Do–Fr Gefrierfach',
    time: 'Sonntag · ca. 90 Min',
    dogAlt: 'Der Chefty-Hund steht hinter vier fertigen Lunchboxen',
  },

  proteinSec: {
    kicker: 'Protein, als Zahl',
    h2: 'Bei jedem Gericht steht das Protein.',
    lead: 'Protein pro Mittagessen im Schnitt, in der Beispielwoche oben.',
    body: 'Jedes Gericht zeigt seine Gramm pro Portion, und die Woche zeigt deine Summe. Kein Tracken, kein Tagebuch.',
    barsLabel: 'Protein pro Mittagessen',
    goalsLabel: (dish) => `Eine Portion ${dish} je nach Ziel`,
    foot: (dish) =>
      `${dish}, eine Portion, je nach Ziel. Die Werte sind Schätzungen pro Portion und keine medizinische oder Ernährungsberatung.`,
  },

  recipes: {
    kicker: 'Deine eigenen Rezepte',
    h2: 'Rezepte überall gespeichert? Bring sie mit.',
    importH: 'Von TikTok, Instagram, YouTube oder jeder Rezeptseite.',
    importP: 'Füg den Link ein, Chefty holt das Rezept raus. Ein Screenshot oder Text geht auch.',
    reviewH: 'Gespeichert wird erst, wenn du es geprüft hast.',
    reviewP: 'Damit aus einem falsch gelesenen „1 EL“ nie „1 Tasse“ wird.',
    reviewLabel:
      'Der Prüfen-Screen: Der Hund sagt Alles gefunden. Du prüfst, ich warte. Darunter der Entwurf für Shakshuka mit bearbeitbaren Zutaten.',
    listH: 'Die Einkaufsliste, nach Gängen sortiert.',
    listP: 'Damit der Prep-Tag mit einem Einkauf beginnt, nicht mit dreien.',
    listHead: 'Alles für deine Woche.',
    items: 'Sachen.',
    counter: (n, total) => `${n} von ${total}`,
    aisles: { produce: 'Gemüse', chilled: 'Kühlregal' },
    shopping: {
      springOnion: 'Frühlingszwiebel',
      ginger: 'Ingwer',
      cucumber: 'Gurke',
      chicken: 'Hähnchenschenkel',
      feta: 'Feta',
    },
    factsH: 'Faire Bedingungen.',
    facts: [
      ['Ohne Konto starten.', ' Melde dich mit Apple, Google oder E-Mail an, wenn du Sync willst.'],
      ['Gespeicherte Rezepte gehen offline.', ''],
      ['Keine Werbung, in keinem Plan.', ''],
      ['Analytics nur mit deinem Ja.', ' Dein Bildschirm wird nie aufgezeichnet.'],
      ['Konto in der App löschen.', ''],
      ['Englisch und Deutsch.', ''],
    ],
    smallprint:
      'Deine importierten Rezepte leben in deinem Rezeptbuch und im Kochmodus. In die Prep-Woche fließen sie noch nicht ein.',
  },
  shakshuka: {
    title: 'Shakshuka',
    hello: 'Alles gefunden. Du prüfst, ich warte.',
    link: 'Link',
    readByAi: 'Von KI gelesen. Tippen zum Ändern.',
    ingredients: 'Zutaten',
    method: 'Zubereitung',
    addIngredient: 'Zutat hinzufügen',
    items: [
      ['4', 'Eier'],
      ['400 g', 'Gehackte Tomaten'],
      ['1', 'Rote Paprika'],
      ['1 TL', 'Kreuzkümmel'],
      ['½', 'Bund Petersilie'],
    ],
    steps: [
      'Zwiebel und Paprika 5 Minuten in Olivenöl andünsten.',
      'Kreuzkümmel und Tomaten dazugeben. 10 Minuten köcheln lassen.',
      'Vier Mulden machen und die Eier hineinschlagen.',
    ],
    save: 'Speichern',
    summary: (ingredients, steps) => `${ingredients} Zutaten · ${steps} Schritte`,
  },
  importDemo: {
    sources: {
      tiktok: 'TikTok',
      instagram: 'Instagram',
      youtube: 'YouTube',
      web: 'Rezeptseiten',
      photo: 'Fotos',
      text: 'Text',
    },
    demos: [
      {
        source: 'tiktok',
        link: 'tiktok.com/@yourfavcook/video/7391827465',
        title: 'Shakshuka',
        items: [
          ['4', 'Eier'],
          ['400 g', 'Gehackte Tomaten'],
          ['1', 'Rote Paprika'],
          ['1 TL', 'Kreuzkümmel'],
          ['½', 'Bund Petersilie'],
        ],
        steps: 6,
      },
      {
        source: 'instagram',
        link: 'instagram.com/reel/C9f2kLmQx4p',
        title: 'Ofen-Feta-Pasta',
        items: [
          ['200 g', 'Feta'],
          ['400 g', 'Kirschtomaten'],
          ['250 g', 'Nudeln'],
          ['2', 'Knoblauchzehen'],
          ['', 'Basilikum'],
        ],
        steps: 4,
      },
      {
        source: 'youtube',
        link: 'youtube.com/shorts/x8Qp3TvLm2c',
        title: 'Overnight Oats',
        items: [
          ['50 g', 'Haferflocken'],
          ['150 ml', 'Milch'],
          ['100 g', 'Joghurt'],
          ['1 EL', 'Chiasamen'],
          ['', 'Beeren'],
        ],
        steps: 3,
      },
      {
        source: 'web',
        link: 'grandmas-kitchen-tales.com/banana-bread',
        title: 'Bananenbrot',
        items: [
          ['3', 'Reife Bananen'],
          ['250 g', 'Mehl'],
          ['100 g', 'Butter'],
          ['2', 'Eier'],
          ['1 TL', 'Natron'],
        ],
        steps: 6,
      },
    ],
    placeholder: 'Link hier einfügen',
    paste: 'Einfügen',
    import: 'Importieren',
    stages: ['Senden', 'Lesen', 'Fertig'],
    lines: {
      send: 'Sende…',
      read: 'Lese Zutaten und Schritte…',
      done: 'Fertig.',
      usually: 'Meist unter 30 Sekunden.',
    },
    label:
      'Rezept-Links von TikTok, Instagram, YouTube und einer Rezeptseite werden nacheinander eingefügt und importiert. Chefty liest Zutaten und Schritte und zeigt dir jeden Entwurf zum Prüfen.',
  },

  cook: {
    kicker: 'Kochmodus',
    h2: 'Ein Schritt pro Screen. Die Zutaten, die du brauchst, stehen direkt da.',
    list: [
      ['Wisch zum nächsten Schritt.', 'Große Schrift, lesbar vom Herd aus.'],
      ['Tipp auf eine Zeit, und der Timer startet.', '„8 Min“ im Text ist ein Button.'],
      ['Leg das Handy weg.', 'Es merkt sich, wo du warst.'],
    ],
    phoneLabel: 'Kochmodus für Shakshuka. Mit Zurück und Weiter gehst du durch die Schritte.',
    counter: (n, total) => `Schritt ${n} von ${total}`,
    stepLabel: (n) => `Shakshuka · Schritt ${n}`,
    timerStep: (n) => `· Schritt ${n}`,
    reach: 'Das brauchst du jetzt',
    steps: [
      {
        text: ['Zwiebel und Paprika in Olivenöl andünsten,', '5 Min', '.'],
        reach: [
          ['1', 'Zwiebel'],
          ['1', 'Rote Paprika'],
          ['', 'Olivenöl'],
        ],
      },
      {
        text: ['Kreuzkümmel einrühren und mitrösten,', '1 Min', '.'],
        reach: [['1 TL', 'Kreuzkümmel']],
      },
      {
        text: ['Tomaten dazugeben und köcheln lassen,', '10 Min', '.'],
        reach: [['400 g', 'Gehackte Tomaten']],
      },
      {
        text: ['Die Eier in die Soße schlagen, abdecken und garen,', '8 Min', '.'],
        reach: [['4', 'Eier']],
      },
      { text: ['Die Petersilie darüberstreuen.', '', ''], reach: [['½', 'Bund Petersilie']] },
      {
        text: ['Mit Salz und Pfeffer abschmecken und in der Pfanne servieren.', '', ''],
        reach: [
          ['', 'Salz'],
          ['', 'Pfeffer'],
        ],
      },
    ],
    back: 'Zurück',
    next: 'Nächster Schritt',
    finish: 'Fertig',
    startOver: 'Von vorn',
  },

  pricing: {
    kicker: 'Preise',
    h2: 'Kostenlos planen. Zahlen nur für unbegrenzte Importe.',
    free: 'Kostenlos',
    freeLines: (imports, days) => [
      'Deine Prep-Woche und die Einkaufsliste',
      'Kochmodus und dein Rezeptbuch',
      `${imports} Rezeptimporte alle ${days} Tage`,
      'Gespeicherte Rezepte bleiben immer offen',
    ],
    plus: 'Chefty Plus',
    perMonth: 'im Monat',
    alt: (yearly, perMonth, trialDays) =>
      `oder ${yearly} im Jahr (${perMonth} im Monat), ${trialDays} Tage gratis testen`,
    plusLines: [
      'Alles aus Kostenlos',
      'Unbegrenzte Rezeptimporte',
      'Jederzeit in deinem Apple-Account kündbar',
    ],
    foot: 'Abrechnung und Kündigung über den App Store, in deiner App-Store-Währung.',
  },

  faq: {
    kicker: 'Fragen',
    h2: 'Bevor du preppst.',
    write: 'Noch was? Schreib an',
    items: (imports, days) => [
      {
        q: 'Muss ich jeden Tag kochen?',
        a: 'Nein. Du kochst einmal an deinem Prep-Tag. An den anderen Tagen kombinierst du, was im Kühlschrank steht, zu einem anderen Gericht.',
      },
      {
        q: 'Wie lange hält vorgekochtes Essen?',
        a: 'Chefty plant etwa drei Tage im Kühlschrank. Mittagessen danach kommen am Prep-Tag ins Gefrierfach, und die Woche zeigt dir, welche.',
      },
      {
        q: 'Kann ich meine eigenen Rezepte in der Prep-Woche nutzen?',
        a: 'Noch nicht. Die Prep-Woche nutzt heute die Basen von Chefty. Deine importierten Rezepte landen in deinem Rezeptbuch und im Kochmodus.',
      },
      {
        q: 'Wie genau sind die Proteinwerte?',
        a: 'Es sind Schätzwerte pro Portion für jede Basis. Nimm sie als Orientierung, nicht als Ernährungsberatung.',
      },
      {
        q: 'Ich esse vegetarisch oder keinen Fisch.',
        a: 'Markier, was nie in die Box soll, und Chefty lässt es weg. Allergene prüfst du bitte selbst in den Zutaten.',
      },
      {
        q: 'Welche Rezept-Links funktionieren?',
        a: 'Die meisten Food-Blogs, Magazinseiten und YouTube. TikTok und Instagram klappen, wenn das Rezept in der Beschreibung steht. Ein Screenshot der Zutatenliste geht immer.',
      },
      {
        q: 'Was ist kostenlos?',
        a: `Deine Prep-Woche planen, die Einkaufsliste, der Kochmodus und ${imports} Rezeptimporte alle ${days} Tage. Der Zähler setzt sich von selbst zurück, und gespeicherte Rezepte werden nie gesperrt.`,
      },
      {
        q: 'Brauche ich ein Konto?',
        a: 'Nein. Du kannst sofort loslegen. Melde dich später mit Apple, Google oder E-Mail an, dann kommen deine Rezepte mit.',
      },
      {
        q: 'Gibt es Chefty für Android?',
        a: 'Noch nicht. Chefty gibt es vorerst nur fürs iPhone.',
      },
    ],
  },

  closing: {
    h2: 'Plan heute Abend deine erste Prep-Woche.',
    lede: 'Vier Fragen, drei Basen, eine Liste.',
    dogAlt: 'Der Chefty-Dackel sitzt mit weißer Kochmütze',
  },

  footer: {
    tagline: 'Einmal vorkochen, jeden Tag was anderes essen.',
    product: 'Produkt',
    help: 'Hilfe',
    legal: 'Rechtliches',
    prepWeek: 'Die Prep-Woche',
    how: 'So geht’s',
    pricing: 'Preise',
    faq: 'FAQ',
    support: 'Support',
    deleteAccount: 'Konto löschen',
    privacy: { label: 'Datenschutz', href: '/datenschutz' },
    terms: { label: 'AGB', href: '/agb' },
    cookies: 'Cookie-Einstellungen',
    vat: 'USt-IdNr.',
    responsible: 'Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV:',
    trademarks:
      'App Store und TestFlight sind Marken von Apple Inc. TikTok, Instagram und YouTube sind Marken ihrer jeweiligen Inhaber.',
  },
};

export const COPY: Record<Lang, Copy> = { en, de };
